import React from "react";
import { useTranslation } from "react-i18next";
import WelcomeIllustration from "../components/WelcomeIllustration";
import { DAY_KEYS } from "../utils/schedule";

/** The next four days, starting tomorrow, as the sample page's date chips. */
const sampleDates = (language) => {
  const month = new Intl.DateTimeFormat(language, { month: "short" });
  const today = new Date();
  return [1, 2, 3, 4].map((offset) => {
    const date = new Date(today);
    date.setDate(today.getDate() + offset);
    return {
      // getDay() is Sunday-first; DAY_KEYS is Monday-first.
      dayKey: DAY_KEYS[(date.getDay() + 6) % 7],
      label: `${date.getDate()} ${month.format(date).replace(/\.$/, "")}`,
    };
  });
};

const SAMPLE_TIMES = ["09:00", "10:00", "11:00", "12:00"];

/**
 * Step 1: what the next few minutes are for, beside a sample of where they
 * end up — a booking page with a day and a time picked.
 *
 * The sample carries no business name or service: an invented one read as
 * somebody else's page. The person's own page is built on step 5.
 */
const WelcomeStep = () => {
  const { t, i18n } = useTranslation();
  const language = i18n?.resolvedLanguage || i18n?.language || "ro";

  const overview = [
    { icon: "bi-shop", key: "business" },
    { icon: "bi-people", key: "team" },
    { icon: "bi-calendar-check", key: "page" },
  ];

  let dates = [];
  try {
    dates = sampleDates(language);
  } catch {
    // An unknown locale tag is a RangeError; the chips are decoration.
    dates = sampleDates("ro");
  }

  return (
    <div>
      <div className="text-center">
        <h2 className="m-0 text-2xl font-bold text-ink-900 sm:text-3xl">
          {t("Dashboard.Setup.welcome.title")}
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-fluid-sm text-ink-500">
          {t("Dashboard.Setup.welcome.subtitle")}
        </p>
      </div>

      {/* Side by side from `xl`, not `lg`: at 1024 the sidebar is there too,
          and the card has about 600px, which would leave the list a sliver. */}
      <div className="mx-auto mt-8 grid max-w-4xl items-center gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] xl:gap-12">
        <ol className="m-0 flex list-none flex-col gap-6 p-0">
          {overview.map(({ icon, key }) => (
            <li key={key} className="flex items-start gap-4">
              <span className="flex size-14 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                <i className={`bi ${icon} text-2xl`} aria-hidden="true" />
              </span>
              <span className="min-w-0 pt-1">
                <span className="block text-base font-semibold text-ink-900">
                  {t(`Dashboard.Setup.welcome.items.${key}.title`)}
                </span>
                <span className="mt-0.5 block text-sm text-ink-500">
                  {t(`Dashboard.Setup.welcome.items.${key}.description`)}
                </span>
              </span>
            </li>
          ))}
        </ol>

        {/* The sample booking page. Decoration: nothing in it can be pressed. */}
        <div
          aria-hidden="true"
          className="mx-auto w-full max-w-sm rounded-card border border-ink-200 bg-white p-3 shadow-card"
        >
          <div className="h-32 overflow-hidden rounded-lg sm:h-36">
            <WelcomeIllustration />
          </div>
          <div className="mt-3 grid grid-cols-4 gap-1.5">
            {dates.map((date, index) => (
              <span
                key={date.label}
                className={`flex flex-col items-center rounded-lg border px-1 py-1.5 text-xs leading-tight ${
                  index === 0
                    ? "border-brand-600 bg-brand-600 text-white"
                    : "border-ink-200 text-ink-600"
                }`}
              >
                <span className="font-semibold">
                  {t(`Dashboard.Setup.days.short.${date.dayKey}`)}
                </span>
                <span className="whitespace-nowrap">{date.label}</span>
              </span>
            ))}
          </div>
          <div className="mt-1.5 grid grid-cols-4 gap-1.5">
            {SAMPLE_TIMES.map((time, index) => (
              <span
                key={time}
                className={`rounded-lg border py-1.5 text-center text-xs ${
                  index === 0
                    ? "border-brand-600 bg-brand-600 font-semibold text-white"
                    : "border-ink-200 text-ink-600"
                }`}
              >
                {time}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeStep;
